import { useId, useMemo, useState } from 'react'

import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import {
  Flex,
  PrimaryButton,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { CompactCivilDateField } from '~/components/common/CompactCivilDateField'
import { DsSelect, type DsSelectOption } from '~/components/common/DsSelect'
import { FieldLabel, FieldRoot } from '~/components/common/Field'
import { Modal } from '~/components/common/Modal'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { type CivilDate, compareCivilDate } from '~/models/civilDate'
import {
  type SectionTab,
  addMonths,
  earliestEditableTarget,
  monthOptionValue,
  parseMonthOptionValue,
} from '~/pages/SplitSupplyPlan/models/period'

/** 月間の計画作成で選べる月の数 (来月から)。 */
const SELECTABLE_MONTHS = 12

type Props = {
  isOpen: boolean
  onClose: () => void
  tab: SectionTab
  /** 今日 (JST)。 選べる対象日の下限を決める。 */
  today: CivilDate
  /** 開いた時の対象日・エリアの初期値 (一覧の現在の選択)。 */
  initialTarget: CivilDate
  initialArea: AreaCode | undefined
  isCreating: boolean
  onCreate: (input: { targetDate: CivilDate; areaCode: AreaCode }) => void
}

/**
 * 計画作成のダイアログ。 断面は一覧のタブで決まっている (翌日 / 月間)。 対象日 (月間は対象月)
 * とエリアを選ぶと、 そのエリアの「変動型で自社が通告値を出す契約」 の地点の通告値を作る。
 * 過去の日は選べない (最も早いのは、 翌日の通は明日、 月間の通は来月)。 判断は backend も持つ
 * (NOTICE_DATE_CLOSED) ので、 ここは選べる範囲を絞る UX。
 */
export const CreateNoticesDialog = ({
  isOpen,
  onClose,
  tab,
  today,
  initialTarget,
  initialArea,
  isCreating,
  onCreate,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const idBase = useId()
  const earliest = earliestEditableTarget(tab, today)
  const [target, setTarget] = useState<CivilDate>(
    compareCivilDate(initialTarget, earliest) >= 0 ? initialTarget : earliest,
  )
  const [area, setArea] = useState<AreaCode | undefined>(initialArea)

  const areaOptions: DsSelectOption[] = AREA_CODE_OPTIONS.map((areaCode) => ({
    value: String(areaCode),
    label: convertAreaLabel(areaCode),
  }))
  const monthOptions: DsSelectOption[] = useMemo(
    () =>
      Array.from({ length: SELECTABLE_MONTHS }, (_, i) => {
        const month = addMonths(earliest, i)
        return {
          value: monthOptionValue(month),
          label: t('page:splitSupplyPlan.monthLabel', {
            year: month.year,
            month: month.month,
          }),
        }
      }),
    [earliest, t],
  )

  const targetId = `${idBase}-target`
  const areaId = `${idBase}-area`

  const footer = (
    <Flex justifyContent="flex-end" gap={2} width="100%">
      <TertiaryButton onClick={onClose} size="md">
        {t('common:action.cancel')}
      </TertiaryButton>
      <PrimaryButton
        disabled={area === undefined || isCreating}
        loading={isCreating}
        onClick={() => {
          if (area !== undefined)
            onCreate({ targetDate: target, areaCode: area })
        }}
        size="md"
      >
        {t('page:splitSupplyPlan.createDialog.submit')}
      </PrimaryButton>
    </Flex>
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={t('page:splitSupplyPlan.createDialog.title')}
    >
      <Flex flexDirection="column" gap={4}>
        <Text textStyle="caption" color="Text.midEmphasis">
          {t(
            tab === 'monthly'
              ? 'page:splitSupplyPlan.createDialog.noteMonthly'
              : 'page:splitSupplyPlan.createDialog.noteDayAhead',
          )}
        </Text>
        <FieldRoot>
          <FieldLabel htmlFor={targetId}>
            {t(
              tab === 'monthly'
                ? 'page:splitSupplyPlan.createDialog.targetMonth'
                : 'page:splitSupplyPlan.createDialog.targetDate',
            )}
          </FieldLabel>
          {tab === 'monthly' ? (
            <DsSelect
              aria-label={t('page:splitSupplyPlan.createDialog.targetMonth')}
              inputId={targetId}
              onChange={(value) => {
                const month = parseMonthOptionValue(value)
                if (month !== undefined) setTarget(month)
              }}
              options={monthOptions}
              value={monthOptionValue(target)}
            />
          ) : (
            <CompactCivilDateField
              aria-label={t('page:splitSupplyPlan.createDialog.targetDate')}
              id={targetId}
              min={earliest}
              onChange={(d) => {
                if (d !== undefined && compareCivilDate(d, earliest) >= 0) {
                  setTarget(d)
                }
              }}
              value={target}
            />
          )}
        </FieldRoot>
        <FieldRoot>
          <FieldLabel htmlFor={areaId}>
            {t('page:splitSupplyPlan.createDialog.area')}
          </FieldLabel>
          <DsSelect
            aria-label={t('page:splitSupplyPlan.createDialog.area')}
            inputId={areaId}
            onChange={(value) => {
              setArea(AREA_CODE_OPTIONS.find((a) => String(a) === value))
            }}
            options={areaOptions}
            placeholder={t('common:form.selectPlaceholder')}
            value={area === undefined ? undefined : String(area)}
          />
        </FieldRoot>
      </Flex>
    </Modal>
  )
}
